import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldAlt, faPlus, faEdit, faTrash, faLock, faClose } from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../../utils/apiConfig';
import './PermissionManagement.css';

function PermissionManagement({ userId, refreshTrigger, onRefresh }) {
  const [permissions, setPermissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddSystemPermissionModal, setShowAddSystemPermissionModal] = useState(false);
  const [systemPermissionFormData, setSystemPermissionFormData] = useState({
    key: '',
    name: '',
    description: ''
  });
  const [editingPermission, setEditingPermission] = useState(null);
  const [sortConfig, setSortConfig] = useState({
    key: 'key',
    direction: 'ascending'
  });

  // Add a ref to track if we're currently fetching permissions
  const isFetchingPermissionsRef = useRef(false);

  // Memoize the fetchPermissions function to prevent unnecessary re-creation
  const fetchPermissions = useCallback(async () => {
    // If we're already fetching permissions, don't start another fetch
    if (isFetchingPermissionsRef.current) {
      return;
    }

    try {
      // Set the fetching flag to prevent duplicate API calls
      isFetchingPermissionsRef.current = true;
      setIsLoading(true);

      const response = await api.get(getApiUrl('/api/admin/permissions'), {
        params: { userId }
      });

      if (response.data) {
        setPermissions(response.data);
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setError('Failed to load permissions. Please try again later.');
      setIsLoading(false);
    } finally {
      // Reset the fetching flag
      isFetchingPermissionsRef.current = false;
    }
  }, [userId]);

  // Fetch permissions when the component mounts or refreshTrigger changes
  useEffect(() => {
    // Use a small timeout to debounce multiple rapid calls
    const debounceTimeout = setTimeout(() => {
      fetchPermissions();
    }, 50);

    // Clean up the timeout on unmount or when dependencies change
    return () => {
      clearTimeout(debounceTimeout);
    };
  }, [fetchPermissions, refreshTrigger]);

  // Handle adding a new system permission
  const handleAddSystemPermission = async (e) => {
    e.preventDefault();

    if (!systemPermissionFormData.key || !systemPermissionFormData.name) {
      setError('Permission key and name are required');
      return;
    }

    try {
      const payload = {
        key: systemPermissionFormData.key,
        name: systemPermissionFormData.name,
        description: systemPermissionFormData.description,
        userId
      };

      if (editingPermission) {
        // Update existing permission
        await api.put(getApiUrl(`/api/admin/permissions/${editingPermission.id}`), payload);
      } else {
        // Create new permission
        await api.post(getApiUrl('/api/admin/permissions'), payload);
      }

      // Refresh permissions
      fetchPermissions();

      // Reset form and close modal
      setSystemPermissionFormData({
        key: '',
        name: '',
        description: ''
      });
      setEditingPermission(null);
      setShowAddSystemPermissionModal(false);

      // Trigger refresh of all admin tables
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error adding system permission:', error);
      setError('Failed to add permission: ' + (error.response?.data?.error || error.message));
    }
  };

  // Handle deleting a system permission
  const handleDeleteSystemPermission = async (permissionId) => {
    if (!window.confirm(`Are you sure you want to delete this permission? This will also remove it from any roles that use it.`)) {
      return;
    }

    try {
      await api.delete(getApiUrl(`/api/admin/permissions/${permissionId}`), {
        params: { userId }
      });

      // Refresh permissions
      fetchPermissions();

      // Trigger refresh of all admin tables
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error deleting system permission:', error);
      alert('Failed to delete permission: ' + (error.response?.data?.error || error.message));
    }
  };

  // Handle editing a system permission
  const handleEditSystemPermission = (permission) => {
    setEditingPermission(permission);
    setSystemPermissionFormData({
      key: permission.key,
      name: permission.name,
      description: permission.description || ''
    });
    setShowAddSystemPermissionModal(true);
  };

  // Handle system permission form input changes
  const handleSystemPermissionInputChange = (e) => {
    const { name, value } = e.target;
    setSystemPermissionFormData({
      ...systemPermissionFormData,
      [name]: value
    });
  };

  // Handle sorting when a column header is clicked
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  // Get the sorted data
  const getSortedData = (data) => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      // Handle null or undefined values
      if (a[sortConfig.key] == null) return sortConfig.direction === 'ascending' ? 1 : -1;
      if (b[sortConfig.key] == null) return sortConfig.direction === 'ascending' ? -1 : 1;

      // Handle string fields
      if (typeof a[sortConfig.key] === 'string' && typeof b[sortConfig.key] === 'string') {
        return sortConfig.direction === 'ascending'
          ? a[sortConfig.key].localeCompare(b[sortConfig.key])
          : b[sortConfig.key].localeCompare(a[sortConfig.key]);
      }

      // Handle other fields
      return sortConfig.direction === 'ascending'
        ? a[sortConfig.key] > b[sortConfig.key] ? 1 : -1
        : a[sortConfig.key] < b[sortConfig.key] ? 1 : -1;
    });
  };

  // Get sort direction indicator
  const getSortDirectionIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'ascending' ? ' ↑' : ' ↓';
  };

  // Apply sorting to permissions
  const sortedPermissions = getSortedData(permissions);

  // Add System Permission Modal JSX
  const renderAddSystemPermissionModal = () => {
    if (!showAddSystemPermissionModal) return null;

    return (
      <div className="modal-overlay">
        <div className="modal-content">
          <div className="modal-header">
            <h3>{editingPermission ? 'Edit Permission' : 'Add New Permission'}</h3>
            <button
              type="button"
              className="modal-close-btn"
              onClick={() => {
                setShowAddSystemPermissionModal(false);
                setEditingPermission(null);
              }}
            >
              <FontAwesomeIcon icon={faClose} />
            </button>
          </div>
          <form onSubmit={handleAddSystemPermission}>
            <div className="form-group">
              <label>Permission Key</label>
              <input
                type="text"
                name="key"
                className="form-control"
                value={systemPermissionFormData.key}
                onChange={handleSystemPermissionInputChange}
                required
                placeholder="e.g., view_users, edit_accounts"
                disabled={editingPermission !== null}
              />
              <small className="form-text text-muted">Unique identifier for this permission (cannot be changed later)</small>
            </div>

            <div className="form-group">
              <label>Display Name</label>
              <input
                type="text"
                name="name"
                className="form-control"
                value={systemPermissionFormData.name}
                onChange={handleSystemPermissionInputChange}
                required
                placeholder="e.g., View Users, Edit Accounts"
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                name="description"
                className="form-control"
                value={systemPermissionFormData.description}
                onChange={handleSystemPermissionInputChange}
                placeholder="Describe what this permission allows"
                rows="3"
              />
            </div>



            <div className="modal-actions">
              <button type="submit" className="btn btn-success">
                <FontAwesomeIcon icon={editingPermission ? faEdit : faPlus} />
                {editingPermission ? 'Update Permission' : 'Add Permission'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowAddSystemPermissionModal(false);
                  setEditingPermission(null);
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="admin-card">
        <div className="admin-card-header">
          <h2 className="admin-card-title">
            <FontAwesomeIcon icon={faShieldAlt} className="icon" />
            Permission Management
          </h2>
          <button className="btn btn-primary" onClick={() => {
            setEditingPermission(null);
            setSystemPermissionFormData({
              key: '',
              name: '',
              description: ''
            });
            setShowAddSystemPermissionModal(true);
          }}>
            <FontAwesomeIcon icon={faPlus} /> Add Permission
          </button>
        </div>

        {error && (
          <div className="alert alert-danger">{error}</div>
        )}

        <div className="permissions-section">

          {isLoading ? (
            <div className="loading-container">
              <div className="loading-content">
                {/* <div className="loading-spinner"></div> */}
                <p>Loading permissions...</p>
              </div>
            </div>
          ) : permissions.length === 0 ? (
            <div className="empty-state">
              <FontAwesomeIcon icon={faLock} className="empty-state-icon" />
              <p className="empty-state-text">No custom permissions defined yet</p>
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setEditingPermission(null);
                  setSystemPermissionFormData({
                    key: '',
                    name: '',
                    description: '',
                    resourceType: 'system'
                  });
                  setShowAddSystemPermissionModal(true);
                }}
              >
                <FontAwesomeIcon icon={faPlus} /> Add First Permission
              </button>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th onClick={() => requestSort('key')} className="sortable-header">
                      Key{getSortDirectionIndicator('key')}
                    </th>
                    <th onClick={() => requestSort('name')} className="sortable-header">
                      Name{getSortDirectionIndicator('name')}
                    </th>
                    <th onClick={() => requestSort('description')} className="sortable-header">
                      Description{getSortDirectionIndicator('description')}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedPermissions.map(permission => (
                    <tr key={permission.id}>
                      <td>{permission.key}</td>
                      <td>{permission.name}</td>
                      <td>{permission.description || '-'}</td>
                      <td>
                        <div className="action-buttons">
                          {!permission.is_system && (
                            <>
                              <button
                                className="btn btn-sm btn-secondary"
                                onClick={() => handleEditSystemPermission(permission)}
                                title="Edit Permission"
                              >
                                <FontAwesomeIcon icon={faEdit} />
                              </button>
                              <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleDeleteSystemPermission(permission.id)}
                                title="Delete Permission"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Render modals */}
      {renderAddSystemPermissionModal()}
    </>
  );
}

export default PermissionManagement;
